import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  TeacherAttendanceResponse,
  TeacherAttendanceOverrideRequest,
} from "../../services/teacherAttendanceService";

interface AttendanceOverrideModalProps {
  open: boolean;
  onClose: () => void;
  record: TeacherAttendanceResponse | null;
  onSave: (id: number, payload: TeacherAttendanceOverrideRequest) => Promise<void>;
}

export const AttendanceOverrideModal: React.FC<AttendanceOverrideModalProps> = ({
  open,
  onClose,
  record,
  onSave,
}) => {
  const { t } = useTranslation();
  const [checkInTime, setCheckInTime] = useState<string>("");
  const [checkOutTime, setCheckOutTime] = useState<string>("");
  const [status, setStatus] = useState<string>("PRESENT");
  const [overrideNote, setOverrideNote] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const parseSafeISO = (dateVal: any): Date => {
    if (!dateVal) return new Date();
    
    if (dateVal instanceof Date) {
      return dateVal;
    }

    if (typeof dateVal === 'number') {
      if (dateVal < 10000000000) {
        return new Date(dateVal * 1000);
      }
      return new Date(dateVal);
    }
    
    if (Array.isArray(dateVal)) {
      const [year, month, day, hour = 0, minute = 0, second = 0, nano = 0] = dateVal;
      const ms = Math.floor(nano / 1000000);
      return new Date(year, month - 1, day, hour, minute, second, ms);
    }
    
    const dateStr = String(dateVal).trim();

    // If it's a numeric string (timestamp)
    if (/^\d+(\.\d+)?$/.test(dateStr)) {
      const num = Number(dateStr);
      if (num < 10000000000) {
        return new Date(num * 1000);
      }
      return new Date(num);
    }

    // If there's a space between date and time, replace the first space with 'T'
    let normalized = dateStr;
    if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}/.test(normalized)) {
      normalized = normalized.replace(/\s+/, 'T');
    }

    const date = new Date(normalized);
    if (!isNaN(date.getTime())) {
      return date;
    }

    // Fallback: manual parsing for ISO-like formats
    const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(.*)$/);
    if (match) {
      const [_, y, M, d, h, m, s, msVal, tz] = match;
      const year = parseInt(y, 10);
      const month = parseInt(M, 10) - 1;
      const day = parseInt(d, 10);
      const hour = parseInt(h, 10);
      const minute = parseInt(m, 10);
      const second = parseInt(s, 10);
      const ms = msVal ? parseInt(msVal.substring(0, 3).padEnd(3, '0'), 10) : 0;

      if (tz) {
        const tzClean = tz.trim();
        if (tzClean === 'Z') {
          return new Date(Date.UTC(year, month, day, hour, minute, second, ms));
        }
        const tzMatch = tzClean.match(/^([+-])(\d{2}):?(\d{2})?$/);
        if (tzMatch) {
          const [__, sign, tzH, tzM = '0'] = tzMatch;
          const offsetMinutes = parseInt(tzH, 10) * 60 + parseInt(tzM, 10);
          const offsetMs = offsetMinutes * 60 * 1000 * (sign === '+' ? 1 : -1);
          const utcTime = Date.UTC(year, month, day, hour, minute, second, ms);
          return new Date(utcTime - offsetMs);
        }
      }
      return new Date(year, month, day, hour, minute, second, ms);
    }

    return new Date(dateStr);
  };

  // Helper to parse HH:MM from ISO string
  const getTimeStringFromIso = (isoString: any): string => {
    if (!isoString) return "";
    try {
      const date = parseSafeISO(isoString);
      if (isNaN(date.getTime())) {
        const match = String(isoString).match(/\d{2}:\d{2}/);
        return match ? match[0] : "";
      }
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${hours}:${minutes}`;
    } catch (e) {
      return "";
    }
  };

  const formatDateString = (dateVal: any): string => {
    if (!dateVal) return "";
    if (Array.isArray(dateVal)) {
      const [year, month, day] = dateVal;
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }
    const dateStr = String(dateVal).trim();
    if (dateStr.includes(",")) {
      const parts = dateStr.split(",");
      if (parts.length === 3) {
        return `${parts[0]}-${parts[1].padStart(2, "0")}-${parts[2].padStart(2, "0")}`;
      }
    }
    return dateStr.split("T")[0];
  };

  // Helper to combine YYYY-MM-DD and HH:MM into local offset ISO string
  const combineDateAndTime = (dateStr: string, timeStr: string): string => {
    if (!timeStr) return "";
    const localDate = new Date(`${dateStr}T${timeStr}:00`);
    const offsetMin = localDate.getTimezoneOffset();
    const offsetSign = offsetMin <= 0 ? "+" : "-";
    const absOffsetMin = Math.abs(offsetMin);
    const offsetHours = String(Math.floor(absOffsetMin / 60)).padStart(2, "0");
    const offsetMinutes = String(absOffsetMin % 60).padStart(2, "0");
    return `${dateStr}T${timeStr}:00${offsetSign}${offsetHours}:${offsetMinutes}`;
  };

  useEffect(() => {
    if (record) {
      setCheckInTime(getTimeStringFromIso(record.checkInTime) || "09:00");
      setCheckOutTime(getTimeStringFromIso(record.checkOutTime) || "17:00");
      setStatus(record.status || "PRESENT");
      setOverrideNote(record.overrideNote || "");
      setError(null);
    }
  }, [record, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!record) return;

    if (!overrideNote.trim()) {
      setError(t("override_note_required") || "Override reason/note is required");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Statuses like ABSENT and LEAVE might not need times
      const needTimes = status !== "ABSENT" && status !== "LEAVE";
      
      const payload: TeacherAttendanceOverrideRequest = {
        checkInTime: needTimes ? combineDateAndTime(formatDateString(record.date), checkInTime) : "",
        checkOutTime: needTimes ? combineDateAndTime(formatDateString(record.date), checkOutTime) : "",
        status,
        overrideNote,
      };

      await onSave(record.id, payload);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || t("failed_to_save_override") || "Failed to save override");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {t("override_attendance", "Override Attendance Record")}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t("teacher", "Teacher")}: <strong>{record?.staffName}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("date", "Date")}: <strong>{formatDateString(record?.date)}</strong>
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("status", "Status")}</InputLabel>
                <Select
                  value={status}
                  label={t("status", "Status")}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <MenuItem value="PRESENT">{t("PRESENT", "Present")}</MenuItem>
                  <MenuItem value="LATE">{t("LATE", "Late")}</MenuItem>
                  <MenuItem value="HALF_DAY">{t("HALF_DAY", "Half Day")}</MenuItem>
                  <MenuItem value="INCOMPLETE">{t("INCOMPLETE", "Incomplete")}</MenuItem>
                  <MenuItem value="ABSENT">{t("ABSENT", "Absent")}</MenuItem>
                  <MenuItem value="LEAVE">{t("LEAVE", "Leave")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {status !== "ABSENT" && status !== "LEAVE" && (
              <>
                <Grid item xs={6}>
                  <TextField
                    type="time"
                    label={t("check_in_time", "Check In Time")}
                    value={checkInTime}
                    onChange={(e) => setCheckInTime(e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    type="time"
                    label={t("check_out_time", "Check Out Time")}
                    value={checkOutTime}
                    onChange={(e) => setCheckOutTime(e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <TextField
                label={t("override_reason", "Override Reason (Note)")}
                value={overrideNote}
                onChange={(e) => setOverrideNote(e.target.value)}
                fullWidth
                multiline
                rows={3}
                size="small"
                required
                placeholder={t("reason_placeholder", "e.g., Forgot to scan QR code, late due to school bus delay.")}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} disabled={loading}>
            {t("cancel", "Cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {t("save", "Save Changes")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
